import { useState } from 'react'
import { Avatar, Badge, Button } from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { formatDay, initials } from '../format'
import type { CohortFinding, CohortPatient } from '../hooks'
import { RISK_LEVEL_LABELS } from '../labels'

const RISK_COLORS = { high: 'red', medium: 'yellow' } as const

function Reason({ reason }: { reason: CohortFinding }) {
  return (
    <li className="rounded border-l-2 border-blue-300 bg-blue-50/40 px-2 py-1.5">
      <div className="text-sm font-medium">
        {reason.measure.label}
        <Badge ml={6} size="xs" variant="light" color={RISK_COLORS[reason.grade]}>
          {RISK_LEVEL_LABELS[reason.grade]}
        </Badge>
      </div>
      <p className="text-xs text-gray-700">{reason.finding}</p>
      <p className="mt-1 text-xs text-gray-500">
        <span className="font-medium">Qualifying rule:</span> {reason.qualifyingRule}
      </p>
      <p className="text-xs text-gray-500">
        Opened {formatDay(reason.openedAt)} · last evaluated {formatDay(reason.lastEvaluatedAt)}
      </p>
    </li>
  )
}

/**
 * One cohort patient: who they are and why they're in the cohort. Other open gaps are listed
 * apart, as plain text — they are never a reason for inclusion. The row can be excluded from
 * the selection (the drawer's, in the browser only); an excluded row stays listed, marked,
 * with Include.
 */
export function CohortPatientRow({
  patient,
  excluded,
  onExclude,
  onInclude,
}: {
  patient: CohortPatient
  excluded: boolean
  onExclude: () => void
  onInclude: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [top] = patient.inclusionReasons
  const Chevron = expanded ? IconChevronDown : IconChevronRight
  const name = patient.name ?? 'Unnamed patient'
  const action = excluded ? (
    <Button
      size="compact-xs"
      variant="outline"
      color="red"
      aria-label={`Include ${name}`}
      onClick={onInclude}
    >
      Include
    </Button>
  ) : (
    <Button
      size="compact-xs"
      variant="outline"
      color="gray"
      aria-label={`Exclude ${name}`}
      onClick={onExclude}
    >
      Exclude
    </Button>
  )

  return (
    <li
      className={`border-b border-gray-100 py-3 ${excluded ? 'bg-gray-50' : ''}`}
      aria-label={name}
    >
      <div className="flex items-start gap-3">
        <div className={`flex min-w-0 flex-1 items-start gap-3 ${excluded ? 'opacity-60' : ''}`}>
          <Avatar radius="xl" size="md" color="blue">
            {initials(patient.name)}
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <a
                href={`/patients/${patient.patientId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate font-medium text-blue-700 hover:underline"
              >
                {name}
              </a>
              <Badge size="sm" variant="light" color={RISK_COLORS[patient.riskLevel]}>
                {RISK_LEVEL_LABELS[patient.riskLevel]} risk
              </Badge>
            </div>
            <div className="text-xs text-gray-500">
              {patient.age === null ? 'Age unknown' : `${patient.age} y`}
              {' · '}
              {patient.providerName ?? 'No attributed provider'}
            </div>
            {top && (
              <p className="mt-1 text-sm text-gray-700">
                {top.measure.label}: {top.finding}
              </p>
            )}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-1 flex cursor-pointer items-center gap-1 text-xs font-medium text-blue-700"
            >
              <Chevron size={14} />
              Why included?
            </button>
            {expanded && (
              <ul className="mt-2 flex flex-col gap-1.5" aria-label="Inclusion reasons">
                {patient.inclusionReasons.map((reason) => (
                  <Reason key={`${reason.measure.slug}:${reason.openedAt}`} reason={reason} />
                ))}
              </ul>
            )}
            {patient.otherOpenGaps.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                <span className="font-medium">Other open gaps:</span>{' '}
                <span aria-label="Other open gaps">
                  {patient.otherOpenGaps.map((gap) => gap.measure.label).join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 pt-0.5">{action}</div>
      </div>
      {excluded && <p className="mt-1 pl-12 text-xs font-medium text-red-700">Excluded</p>}
    </li>
  )
}
